import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Phone, Mail, MapPin, Send, Clock, MessageCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { CONTACT_PAGE, COMPANY } from "../config/content";
import Header from "./Header";
import Footer from "./Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
    website: "" // Honeypot
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/inquiries`, formData);
      toast.success("Thank you! Your message has been sent successfully. We'll contact you soon.");
      setFormData({ name: "", email: "", phone: "", company: "", message: "", website: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again or contact us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openWhatsApp = () => {
    const url = `https://wa.me/${COMPANY.whatsappNumber}?text=${encodeURIComponent(COMPANY.whatsappMessage)}`;
    window.open(url, '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <p className="overline text-[#0047FF] mb-4">Contact Us</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 mb-6">
              {CONTACT_PAGE.hero.title}
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {CONTACT_PAGE.hero.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quick Contact Bar */}
      <section className="py-6 bg-[#0047FF]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12 text-white">
            <a href={`tel:${COMPANY.phone}`} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <Phone className="w-5 h-5" />
              {COMPANY.phone}
            </a>
            <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <Mail className="w-5 h-5" />
              {COMPANY.email}
            </a>
            <button onClick={openWhatsApp} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <MessageCircle className="w-5 h-5" />
              WhatsApp Us
            </button>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4">
                {CONTACT_PAGE.form.title}
              </h2>
              <p className="text-slate-600 mb-8">
                {CONTACT_PAGE.form.description}
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    type="text"
                    name="name"
                    placeholder="Your Name *"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="rounded-none"
                  />
                  <Input
                    type="email"
                    name="email"
                    placeholder="Email Address *"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="rounded-none"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="rounded-none"
                  />
                  <Input
                    type="text"
                    name="company"
                    placeholder="Company Name"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="rounded-none"
                  />
                </div>
                <Textarea
                  name="message"
                  placeholder="Your Message *"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  className="rounded-none resize-none"
                />
                
                {/* Honeypot */}
                <div className="absolute -left-[9999px] opacity-0 pointer-events-none" aria-hidden="true">
                  <Input
                    type="text"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full bg-[#0047FF] hover:bg-[#0037CC] text-white rounded-none py-6 text-base"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="loading-spinner"></span>
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      {CONTACT_PAGE.form.submitButton} <Send size={18} />
                    </span>
                  )}
                </Button>
              </form>
            </motion.div>

            {/* Office Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl font-bold text-slate-900 mb-8">
                Our Offices
              </h2>

              <div className="space-y-6">
                {CONTACT_PAGE.offices.map((office, index) => (
                  <div 
                    key={index}
                    className="bg-slate-50 border border-slate-200 p-6 hover:border-[#0047FF] transition-colors"
                  >
                    <h3 className="font-semibold text-slate-900 mb-3">{office.name}</h3>
                    <div className="space-y-2 text-sm text-slate-600">
                      <p className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 mt-1 flex-shrink-0 text-[#0047FF]" />
                        <span>{office.address}</span>
                      </p>
                      <a href={`tel:${office.phone.replace(/\s/g, '')}`} className="flex items-center gap-2 hover:text-[#0047FF]">
                        <Phone className="w-4 h-4 text-[#0047FF]" />
                        {office.phone}
                      </a>
                      <a href={`mailto:${office.email}`} className="flex items-center gap-2 hover:text-[#0047FF]">
                        <Mail className="w-4 h-4 text-[#0047FF]" />
                        {office.email}
                      </a>
                      {office.mapUrl && (
                        <a 
                          href={office.mapUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[#0047FF] hover:underline mt-2"
                        >
                          View on Map →
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Business Hours */}
              <div className="mt-8 bg-[#0047FF] text-white p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="w-6 h-6" />
                  <h3 className="font-semibold text-lg">Business Hours</h3>
                </div>
                <div className="space-y-2 text-sm">
                  <p className="flex justify-between">
                    <span>Monday - Saturday</span>
                    <span>9:00 AM - 6:00 PM</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Sunday</span>
                    <span>Closed</span>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
